import {
  Button,
  Card,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import moment from 'moment';
import 'moment-timezone';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { archiveDailyNoteTreatment } from 'src/config';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { Permissions, usePermission } from 'src/rbac';
import {
  getArchivedDocumentsAction,
  resetArchivedDocuments,
} from 'src/redux/actions/dailySessionNotes';
import { parseDateWithoutTimezone } from 'src/utils/convertDateToSelectedTimeZone';
import { ArchivedBadge, archivedTitleSx } from './archive-note';

type Props = {
  episodeOfCareId?: string;
};

//  dateOfService is a plain calendar date with no time-of-day, so it's parsed as local midnight
//  rather than run through any timezone conversion.
const formatServiceDate = (value: any, pattern: string) => {
  if (!value) return '-';
  const parsed = parseDateWithoutTimezone(String(value).slice(0, 10));
  if (!parsed || Number.isNaN(parsed.valueOf())) return '-';
  return format(parsed, pattern);
};

//  heldOn is a UTC instant; convert to the facility's timezone (same idiom as daily-census.tsx)
//  and format with moment directly so the facility-local day is what actually gets read.
const formatArchivedOn = (value: any, momentPattern: string) => {
  if (!value) return '-';
  const parsed = moment.utc(value);
  if (!parsed.isValid()) return '-';
  const facilityTimeZone = localStorage.getItem('facility_timezone') || '';
  const inFacilityZone = facilityTimeZone ? parsed.tz(facilityTimeZone) : parsed.local();
  return inFacilityZone.format(momentPattern);
};

/**
 * BOPS-151 — Archived Documents, at the foot of the Episode of Care below Associated
 * Screenings.
 *
 * This is the surface that makes "archived, not deleted" true: the note itself is gone from
 * billing and metrics, but the signed PDF stays reachable here, with the record of who
 * archived it, when, and why.
 *
 * Renders nothing at all unless the BOPS-174 toggle is on, the user holds the BOPS-173
 * permission, and the episode has at least one archived note.
 */
const ArchivedDocuments = (props: Props) => {
  const { episodeOfCareId } = props;
  const dispatch = useDispatch();

  const [isArchiveEnabled] = useFeatureToggleWhenReady(archiveDailyNoteTreatment, 'ENTERPRISE');
  const hasArchivePermission = usePermission(Permissions.ARCHIVE_DAILY_NOTE);
  const canSeeArchived = isArchiveEnabled === true && hasArchivePermission;

  const archivedDocuments = useSelector((state: any) => state?.getArchivedDocumentsReducer);

  useEffect(() => {
    if (!canSeeArchived || !episodeOfCareId) {
      //  Toggle flipped off or permission revoked mid-session: drop the list rather than
      //  leaving a stale section on screen.
      dispatch(resetArchivedDocuments());
      return;
    }
    dispatch(getArchivedDocumentsAction({ episodeOfCareId }));
  }, [canSeeArchived, episodeOfCareId, dispatch]);

  const rows = Array.isArray(archivedDocuments?.response?.data)
    ? archivedDocuments?.response?.data
    : Array.isArray(archivedDocuments?.response)
    ? archivedDocuments?.response
    : [];

  //  Only renders when the episode has at least one archived note — no empty state, the
  //  section simply is not there.
  if (!canSeeArchived || rows.length === 0) return null;

  return (
    <Card sx={{ padding: '1.5rem' }}>
      <Card>
        <Grid
          container
          sx={{ padding: '18px 24px 0px 24px', position: 'relative' }}
          gap="1rem"
          flexDirection="row"
          alignItems={'center'}
        >
          <Typography variant="h6">Archived Documents</Typography>
          <Stack
            flexDirection="row"
            sx={{
              borderRadius: '12px',
              display: 'flex',
              border: '1px solid #00000042',
              color: '#111927',
              padding: '3px 10px',
            }}
          >
            <Typography variant="subtitle2">
              {rows.length === 1 ? '1 Document' : `${rows.length} Documents`}
            </Typography>
          </Stack>
        </Grid>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Date of service</TableCell>
              <TableCell>Therapist</TableCell>
              <TableCell>Archived by</TableCell>
              <TableCell>Archived on</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any, index: number) => (
              <TableRow key={row?.s3DocumentMetadataId || `${row?.dateOfService}-${index}`}>
                <TableCell>
                  <Grid
                    container
                    alignItems="center"
                    direction="row"
                  >
                    <Typography
                      variant="subtitle2"
                      sx={archivedTitleSx}
                    >
                      {row?.documentType || 'Daily SOAP Note'}
                    </Typography>
                    <ArchivedBadge />
                  </Grid>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2">
                    {formatServiceDate(row?.dateOfService, 'dd MMM, yyyy')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2">{row?.therapist || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2">{row?.heldBy || '-'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2">
                    {formatArchivedOn(row?.heldOn, 'DD MMM, YYYY')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2">{row?.reason || '-'}</Typography>
                </TableCell>
                <TableCell align="right">
                  {/*  documentUrl is a ready-to-open link to the PDF the delete retained.
                      Without one there is nothing to open, so the link is not offered — the
                      row still stands as the record of the archive. */}
                  {row?.documentUrl ? (
                    <Button
                      size="small"
                      href={row.documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View document
                    </Button>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{ color: '#6C737F' }}
                    >
                      Document unavailable
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Card>
  );
};

export default ArchivedDocuments;
